/**
 * Validates the complete group name with the following rules:
 * - Must not be empty
 * - Must contain at least 3 parts (category.subcategory.suffix)
 * - Each part can only contain alphanumeric characters
 * - Parts are separated by single dots
 * - Cannot start or end with a dot
 * - No consecutive dots allowed
 *
 * @param {string} groupName - The full group name to validate
 * @param {string} prefix - The prefix to show in the validation message
 * @param {Array} [allGroups=[]] - An array of all resource group objects to check for duplicate names.
 * @param {number} [currentIndex] - The index of the current group being validated in the `allGroups` array.
 * @returns {{isValid: boolean, message: string}}
 */
export const validateGroupName = (groupName, prefix = '', allGroups = [], currentIndex) => {
    // Check for duplicate group names
    if (groupName && allGroups.length > 1) {
        const isDuplicate = allGroups.some(
            (group, index) => index !== currentIndex && group.groupName && group.groupName.toLowerCase() === groupName.toLowerCase()
        );
        if (isDuplicate) {
            return {
                isValid: false,
                message: 'This group name is already in use. Please enter a unique name.',
            };
        }
    }
    // Check if group name exists
    if (!groupName || groupName.trim() === '') {
        return {
            isValid: false,
            message: 'Group Name is required',
        };
    }

    // Regex pattern explanation:
    // ^[a-zA-Z0-9]+ - starts with alphanumeric
    // (\.[a-zA-Z0-9]+){2,} - followed by at least 2 groups of dot + alphanumeric
    // $ - end of string
    const groupNamePattern = /^[a-zA-Z0-9]+(\.[a-zA-Z0-9]+){2,}$/;

    if (!groupNamePattern.test(groupName)) {
        return {
            isValid: false,
            message: `Format: ${prefix}----.----.----.---- (Any number of alphanumeric characters separated by dots. Prefix is read-only)`,
        };
    }

    // Check for maximum segments
    const parts = groupName.split('.');
    if (parts.length > 9) {
        return {
            isValid: false,
            message: 'Group Name cannot have more than 7 segments after the prefix.',
        };
    }

    // Check for suffix length
    if (prefix && groupName.startsWith(prefix)) {
        const suffix = groupName.substring(prefix.length);
        if (suffix.length < 3) {
            return {
                isValid: false,
                message: 'Group Name suffix must be at least 3 characters.',
            };
        }
        if (groupName.length > 64) {
            return {
                isValid: false,
                message: 'Group Name (including prefix) cannot be more than 64 characters.',
            };
        }
    }

    return {
        isValid: true,
        message: '',
    };
};
export default {
    validateGroupName,
};
